/**
 * TeamSystem Impact - Auth Store
 * Zustand store per gestione stato autenticazione
 */

import { create } from 'zustand';
import type { Ruolo, Lingua } from '@teamsystem-impact/shared';
import { logger } from '@/config/logger';

interface Utente {
  id: string;
  nome: string;
  cognome: string;
  email: string;
  ruolo: Ruolo;
  lingua: Lingua;
  azienda: {
    id: string;
    ragioneSociale: string;
  };
}

interface AuthState {
  // State
  utente: Utente | null;
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  // Actions
  setUtente: (utente: Utente) => void;
  setAuth: (token: string, utente: Utente, refreshToken?: string) => void;
  logout: () => void;
  setLoading: (loading: boolean) => void;
  updateLingua: (lingua: Lingua) => void;
}

// Helper per leggere da localStorage SINCRONAMENTE all'avvio
const getInitialState = () => {
  try {
    const stored = localStorage.getItem('teamsystem-impact-auth');
    if (stored) {
      const parsed = JSON.parse(stored);
      logger.debug('AuthStore: loaded from localStorage:', {
        hasToken: !!parsed.token,
        hasUtente: !!parsed.utente,
      });
      return {
        token: parsed.token || null,
        refreshToken: parsed.refreshToken || null,
        utente: parsed.utente || null,
        isAuthenticated: !!(parsed.token && parsed.utente),
        isLoading: false,
      };
    }
  } catch (e) {
    logger.error('AuthStore: error loading from localStorage:', e);
  }
  return {
    utente: null,
    token: null,
    refreshToken: null,
    isAuthenticated: false,
    isLoading: false,
  };
};

export const useAuthStore = create<AuthState>()((set, get) => ({
  // Initial state - caricato SUBITO da localStorage
  ...getInitialState(),

  // Actions
  setUtente: (utente) => {
    logger.debug('setUtente called:', utente.nome);
    const newState = {
      utente,
      isAuthenticated: true,
      isLoading: false,
    };
    set(newState);

    // Salva in localStorage manualmente
    const state = get();
    localStorage.setItem('teamsystem-impact-auth', JSON.stringify({
      token: state.token,
      refreshToken: state.refreshToken,
      utente: state.utente,
    }));
  },

  setAuth: (token, utente, refreshToken?) => {
    logger.debug('setAuth called:', { token: token.substring(0, 20) + '...', utente: utente.nome });
    const newState = {
      token,
      refreshToken: refreshToken ?? get().refreshToken,
      utente,
      isAuthenticated: true,
      isLoading: false,
    };
    set(newState);

    // Salva in localStorage manualmente
    localStorage.setItem('teamsystem-impact-auth', JSON.stringify({
      token,
      refreshToken: newState.refreshToken,
      utente,
    }));
  },

  logout: () => {
    logger.debug('logout called');
    set({
      utente: null,
      token: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
    });

    // Rimuovi da localStorage
    localStorage.removeItem('teamsystem-impact-auth');
  },

  setLoading: (loading) =>
    set({ isLoading: loading }),

  updateLingua: (lingua) => {
    set((state) => ({
      utente: state.utente
        ? { ...state.utente, lingua }
        : null,
    }));

    // Salva in localStorage
    const state = get();
    if (state.token && state.utente) {
      localStorage.setItem('teamsystem-impact-auth', JSON.stringify({
        token: state.token,
        refreshToken: state.refreshToken,
        utente: state.utente,
      }));
    }
  },
}));

